/**
 * What the doctor sees of the call-quality ladder (lib/call/call-quality.ts):
 * signal bars, one status banner, and the Audio only control. The patient app
 * has the same pieces in mobile/components/call/CallQualityUI.tsx.
 */
import { AudioLines, Loader2, Video, VideoOff } from 'lucide-react';
import { signalBars, type CallQualityState, type Signal } from '../../lib/call/call-quality';

const SIGNAL_LABEL: Record<Signal, string> = {
  excellent: 'Connection: excellent',
  good: 'Connection: good',
  poor: 'Connection: weak',
  lost: 'Connection: lost',
  unknown: 'Connection: checking',
};

export function SignalIndicator({ signal }: { signal: Signal }) {
  const bars = signalBars(signal);
  const on = bars >= 2 ? 'bg-jade' : bars === 1 ? 'bg-saffron' : 'bg-alert';
  return (
    <span role="img" aria-label={SIGNAL_LABEL[signal]} title={SIGNAL_LABEL[signal]} className="inline-flex items-end gap-0.5">
      {[1, 2, 3].map(n => (
        <span
          key={n}
          className={`w-[3px] rounded-[1px] ${n <= bars ? on : 'bg-ivory/30'}`}
          style={{ height: 3 + n * 3 }}
        />
      ))}
    </span>
  );
}

/** One message at a time, most urgent first: reconnecting, a lost camera, this
 *  side on audio only, then the patient on audio only. Nothing when all is well. */
export function CallStatusBanner({
  state,
  onTurnVideoBackOn,
}: {
  state: CallQualityState;
  onTurnVideoBackOn: () => void;
}) {
  if (state.reconnecting) {
    return (
      <div role="status" className="flex items-center gap-2 bg-ink/90 px-3 py-1.5 font-body text-caption text-ivory">
        <Loader2 size={13} className="animate-spin" />
        Reconnecting…
      </div>
    );
  }

  if (state.cameraLost) {
    return (
      <div role="status" className="flex items-center gap-2 bg-ink/90 px-3 py-1.5 font-body text-caption text-ivory">
        <VideoOff size={13} />
        Camera disconnected — video comes back when a camera is connected.
      </div>
    );
  }

  if (state.level === 3) {
    const message = state.videoCanReturn
      ? 'Your connection is better.'
      : state.audioOnlyReason === 'weak'
        ? 'Weak connection — switched to audio only'
        : 'Audio only';
    return (
      <div role="status" className="flex flex-wrap items-center gap-2 bg-ink/90 px-3 py-1.5 font-body text-caption text-ivory">
        <AudioLines size={13} />
        <span className="min-w-0 flex-1">{message}</span>
        <button
          type="button"
          onClick={onTurnVideoBackOn}
          className="rounded-md bg-jade px-2 py-0.5 font-semibold text-white transition-opacity hover:opacity-90"
        >
          Turn video back on
        </button>
      </div>
    );
  }

  if (state.peerAudioOnly) {
    return (
      <div role="status" className="flex items-center gap-2 bg-ink/90 px-3 py-1.5 font-body text-caption text-ivory">
        <AudioLines size={13} />
        {state.peerAudioOnly === 'weak'
          ? 'Patient’s connection is weak — they’re on audio only'
          : 'Patient switched to audio only'}
      </div>
    );
  }

  return null;
}

/** Control-row button: switch to audio only, or back to video. */
export function AudioOnlyButton({ audioOnly, onClick }: { audioOnly: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={audioOnly}
      title={audioOnly ? 'Turn video back on' : 'Switch to audio only'}
      className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 font-body text-caption font-semibold text-ivory transition-colors ${
        audioOnly ? 'bg-jade hover:opacity-90' : 'bg-white/10 hover:bg-white/20'
      }`}
    >
      {audioOnly ? <Video size={14} /> : <AudioLines size={14} />}
      {audioOnly ? 'Video' : 'Audio only'}
    </button>
  );
}
